/**
 * The week planner: schedule routes onto days, review and edit the suggested pot for every
 * spot, then load the car. Everything here is intent; nothing is written to the record.
 */
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CircleCheck, CircleDashed, CornerDownRight, Minus, OctagonX, Plus, Redo2, Route as RouteIcon, TriangleAlert, Undo2 } from "lucide-react";
import type { CalendarDate, Catalog, PlannedDay, PlanProblem, WeekPlan } from "@hitl/rotations-domain";
import type { PlannerStore } from "../state/plannerStore";
import { clearPins, deleteRoute, placeRoute, setSpares, sparesOn, weekDays, type PlannerState } from "../state/plannerState";
import { formatDay } from "../scan/copy";
import { pressToast, toastFor, toastText, type UndoToast } from "../state/undoToast";
import { Calendar } from "./Calendar";
import { RoutePickerSheet, RoutesSheet, SpotSheet } from "./sheets";
import { spareFitsLine } from "./spareFits";

export interface PlanFixture {
  readonly selectOffset?: number;
  readonly folded?: boolean;
  readonly sheet?: "spot" | "route-picker" | "routes" | "route-editor";
  readonly spotIndex?: number;
  readonly toast?: string;
  /** Show the toast in its "after Undo" state (offering Redo). */
  readonly toastRedo?: boolean;
  /** Perform one real edit on open (through act), to show its toast + Undo. */
  readonly act?: "delete-route" | "place-route";
}

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function weekdayName(on: CalendarDate): string {
  const [y, m, d] = on.split("-").map(Number) as [number, number, number];
  return WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

const LIGHT = {
  green: { icon: CircleCheck, word: "Fine" },
  amber: { icon: TriangleAlert, word: "Check" },
  red: { icon: OctagonX, word: "Not allowed" },
} as const;

function problemText(p: PlanProblem, catalog: Catalog): string {
  const where = (slotId: string) => {
    const slot = catalog.slot(slotId);
    return `${catalog.client(slot.clientId)?.name ?? slot.clientId}, ${slot.label}`;
  };
  switch (p.code) {
    case "slot-unfilled":
      return `${where(p.slotId)} needs a pot`;
    case "red-verdict":
      return `${p.potId} isn't allowed at ${where(p.slotId)}`;
    case "pot-not-in-car":
      return `${p.potId} won't be in the car for ${where(p.slotId)}`;
    case "pot-used-twice":
      return `${p.potId} is planned for two spots`;
    case "slot-visited-twice":
      return `${where(p.slotId)} is on the route twice`;
    case "spare-not-in-stock":
      return `Spare ${p.potId} isn't in stock`;
  }
}

const problemSlot = (p: PlanProblem): string | undefined => ("slotId" in p ? p.slotId : "slotIds" in p ? p.slotIds[0] : undefined);

export function PlanScreen(props: {
  catalog: Catalog;
  today: CalendarDate;
  week: WeekPlan;
  planner: PlannerState;
  store: PlannerStore;
  onBack: () => void;
  onLoad: (on: CalendarDate) => void;
  defaultSpares?: number;
  fixture?: PlanFixture;
}) {
  const { catalog, today, week, planner, store, fixture } = props;
  const firstPlanned = week.days[0]?.on ?? today;
  const initial = fixture?.selectOffset !== undefined ? week.days[fixture.selectOffset]?.on ?? firstPlanned : firstPlanned;
  const [selected, setSelected] = useState<CalendarDate>(initial);
  const [month, setMonth] = useState<CalendarDate>(initial);
  const [folded, setFolded] = useState(fixture?.folded ?? true);
  const [sheet, setSheet] = useState<{ kind: "spot"; index: number } | { kind: "route-picker" } | { kind: "routes" } | null>(
    fixture?.sheet === "spot"
      ? { kind: "spot", index: fixture.spotIndex ?? 0 }
      : fixture?.sheet === "route-editor"
        ? { kind: "routes" }
        : fixture?.sheet
          ? { kind: fixture.sheet }
          : null,
  );

  const day: PlannedDay | undefined = week.days.find((d) => d.on === selected);
  const route = planner.routes.find((r) => r.id === planner.schedule[selected]);
  const selectedWeek = new Set<string>(weekDays(selected));
  const weekPlanned = week.days.filter((d) => selectedWeek.has(d.on));
  const attention = useMemo(() => new Set(week.problems.map((p) => p.on)), [week.problems]);

  const readout = weekPlanned.reduce(
    (r, d) => {
      for (const s of d.report.swaps) {
        r.swaps += 1;
        if (!s.potId) r.open += 1;
        else if (s.verdict?.light === "green") r.fine += 1;
        else if (s.verdict?.light === "amber") r.check += 1;
        else if (s.verdict?.light === "red") r.red += 1;
      }
      return r;
    },
    { swaps: 0, fine: 0, check: 0, red: 0, open: 0 },
  );
  const weekProblems = week.problems.filter((p) => selectedWeek.has(p.on));

  // Every planner edit says what it did and can be undone (the world's white toast + Undo).
  const [toast, setToast] = useState<UndoToast<PlannerState> | null>(
    fixture?.toast ? { ...toastFor(fixture.toast, planner, 1), ...(fixture.toastRedo ? { action: "redo" as const } : {}) } : null,
  );
  const act = (text: string, change: (s: PlannerState) => PlannerState) => {
    const before = store.get();
    store.update(change);
    setToast(toastFor(text, before, Date.now()));
  };
  // Dev fixture only: run one real edit through act() so its toast can be reviewed.
  useEffect(() => {
    const target = planner.routes.find((r) => r.id === "welkom");
    if (fixture?.act === "delete-route" && target) {
      const days = Object.values(planner.schedule).filter((r) => r === target.id).length;
      act(`Deleted ${target.name} and took it off ${days} day${days === 1 ? "" : "s"}`, (s) => deleteRoute(s, target.id));
    }
    if (fixture?.act === "place-route" && target) {
      act(`${target.name} on ${weekdayName(selected)}`, (s) => placeRoute(s, selected, target.id));
    }
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const select = (on: CalendarDate) => {
    setSelected(on);
    setMonth(on);
  };

  const openProblem = (on: CalendarDate, p: PlanProblem) => {
    select(on);
    const slotId = problemSlot(p);
    const index = week.days.find((d) => d.on === on)?.plan.swaps.findIndex((s) => s.slotId === slotId) ?? -1;
    if (index >= 0) setSheet({ kind: "spot", index });
  };

  // Stops: consecutive spots at the same client.
  const stops = day
    ? day.report.swaps.reduce<{ clientId: string; rows: { swap: (typeof day.report.swaps)[number]; index: number }[] }[]>(
        (acc, swap, index) => {
          const last = acc.at(-1);
          if (last && last.clientId === swap.clientId) last.rows.push({ swap, index });
          else acc.push({ clientId: swap.clientId, rows: [{ swap, index }] });
          return acc;
        },
        [],
      )
    : [];
  const pins = planner.pins[selected] ?? {};
  const isPast = selected < today;
  const spares = sparesOn(planner, selected, props.defaultSpares);
  const handOnTo = (potId: string | undefined) => (potId ? day?.report.handOns.find((h) => h.potId === potId) : undefined);

  return (
    <main className="plan">
      {toast && (
        <div className="toast" role="status" key={toast.id}>
          <span>{toastText(toast)}</span>
          <button
            className="toast__undo"
            onClick={() => {
              const { restore, next } = pressToast(toast, store.get(), Date.now());
              store.update(() => restore);
              setToast(next);
            }}
          >
            {toast.action === "undo" ? <Undo2 aria-hidden size={18} strokeWidth={2.5} /> : <Redo2 aria-hidden size={18} strokeWidth={2.5} />}
            {toast.action === "undo" ? "Undo" : "Redo"}
          </button>
        </div>
      )}
      <header className="plan__top">
        <button className="plan__back" onClick={props.onBack}>
          <ChevronLeft aria-hidden size={22} />
          Home
        </button>
        <button className="plan__routes" onClick={() => setSheet({ kind: "routes" })}>
          <RouteIcon aria-hidden size={18} />
          Routes
        </button>
      </header>

      <Calendar
        planner={planner}
        today={today}
        selected={selected}
        month={month}
        folded={folded}
        attention={attention}
        onSelect={select}
        onMonth={setMonth}
        onFold={setFolded}
      />

      {weekPlanned.length > 0 && (
        <section className="readout" aria-label="This week">
          <p className="readout__counts" role="status">
            <span><b>{readout.swaps}</b> swaps</span>
            <span className={readout.fine ? "is-go" : "is-zero"}><b>{readout.fine}</b> fine</span>
            <span className={readout.check ? "is-check" : "is-zero"}><b>{readout.check}</b> check</span>
            {readout.red > 0 && <span className="is-stop"><b>{readout.red}</b> not allowed</span>}
            <span><b>{readout.open}</b> open</span>
          </p>
          {weekProblems.length > 0 && (
            <ul className="problems">
              {weekProblems.map(({ on, problem }, i) => (
                <li key={`${on}-${i}`}>
                  <button onClick={() => openProblem(on, problem)}>
                    <TriangleAlert aria-hidden size={16} />
                    <span>
                      {weekdayName(on).slice(0, 3)} · {problemText(problem, catalog)}
                    </span>
                    <ChevronRight aria-hidden size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="dayplan" aria-label={`Plan for ${formatDay(selected)}`}>
        <div className="dayplan__head">
          <div>
            <h2>{weekdayName(selected)} {formatDay(selected).replace(/ \d{4}$/, "")}</h2>
            <p>{route ? `${route.name} · ${route.slotIds.length} spots` : "No route"}</p>
          </div>
          {!isPast && (
            <button className="link" onClick={() => setSheet({ kind: "route-picker" })}>
              {route ? "Change route" : "Add a route"}
            </button>
          )}
        </div>

        {isPast && <p className="dayplan__note">This day has passed. What happened is in the record.</p>}
        {!isPast && !route && (
          <button className="dayplan__empty" onClick={() => setSheet({ kind: "route-picker" })}>
            <CircleDashed aria-hidden size={22} />
            Place one of your routes on {weekdayName(selected)}
          </button>
        )}
        {day?.started && <p className="dayplan__note">Swaps are being recorded today. Edits apply to the spots not done yet.</p>}

        {!isPast && day && stops.map((stop, n) => (
          <div className="stop" key={`${stop.clientId}-${n}`}>
            <h3 className="stop__name">
              <span className="stop__num">{n + 1}</span>
              {catalog.client(stop.clientId)?.name}
            </h3>
            {stop.rows.map(({ swap, index }) => {
              const light = swap.potId ? (swap.verdict?.light ?? "green") : null;
              const L = light ? LIGHT[light] : null;
              const rideOn = handOnTo(swap.collects);
              return (
                <button key={swap.slotId} className="spot" onClick={() => setSheet({ kind: "spot", index })}>
                  <span className="spot__label">
                    {catalog.slot(swap.slotId).label}
                    {pins[swap.slotId] && <small className="spot__pinned">Your choice</small>}
                  </span>
                  <span className="spot__pot">{swap.potId ?? "—"}</span>
                  {L && light ? (
                    <span className={`tag tag--${light}`}>
                      <L.icon aria-hidden size={14} strokeWidth={2.75} />
                      {L.word}
                    </span>
                  ) : (
                    <span className="tag tag--open">Choose a pot</span>
                  )}
                  <span className="spot__notes">
                    {swap.source === "collected-earlier" && swap.collectedFromSlotId && (
                      <span className="spot__note spot__note--handon">
                        <CornerDownRight aria-hidden size={14} strokeWidth={2.5} />
                        Rides on from {catalog.client(catalog.slot(swap.collectedFromSlotId).clientId)?.name}
                      </span>
                    )}
                    {swap.collects && (
                      <span className="spot__note">
                        Takes away {swap.collects}
                        {rideOn ? ` · it rides on to ${catalog.client(catalog.slot(rideOn.toSlotId).clientId)?.name}` : " · it comes home"}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        ))}

        {!isPast && day && day.report.spares.length > 0 && (
          <div className="stop" aria-label="Spares">
            <h3 className="spares__name">Spares</h3>
            {day.report.spares.map((spare) => {
              const fits = spareFitsLine(catalog, spare, day.plan.swaps.map((s) => s.slotId));
              return (
                <div className="spare" key={spare.potId}>
                  <span className="spare__pot">{spare.potId}</span>
                  <span className={`spare__fits${fits.none ? " is-check" : ""}`}>{fits.text}</span>
                </div>
              );
            })}
          </div>
        )}

        {!isPast && route && Object.keys(pins).length > 0 && (
          <div className="dayplan__resuggest">
            <button className="link" onClick={() => act(`Suggestions back for ${weekdayName(selected)}`, (s) => clearPins(s, selected, "day"))}>
              Re-suggest {weekdayName(selected)}
            </button>
            <button className="link" onClick={() => act(`Suggestions back from ${weekdayName(selected)} on`, (s) => clearPins(s, selected, "rest"))}>
              Re-suggest from {weekdayName(selected)} on
            </button>
          </div>
        )}
      </section>

      {!isPast && route && day && (
        <footer className="plan__bar">
          <div className="stepper" role="group" aria-label="Spare pots">
            <button aria-label="One spare fewer" onClick={() => store.update((s) => setSpares(s, selected, spares - 1))}>
              <Minus aria-hidden size={18} />
            </button>
            <span className="stepper__value">
              <b>{spares}</b>
              <small>spare{spares === 1 ? "" : "s"}</small>
            </span>
            <button aria-label="One spare more" onClick={() => store.update((s) => setSpares(s, selected, spares + 1))}>
              <Plus aria-hidden size={18} />
            </button>
          </div>
          <button className="btn btn--light plan__load" onClick={() => props.onLoad(selected)}>
            Load {weekdayName(selected).slice(0, 3)}'s car
          </button>
        </footer>
      )}

      {sheet?.kind === "spot" && day && route && (
        <SpotSheet catalog={catalog} day={day} index={sheet.index} pinnedPot={pins[day.plan.swaps[sheet.index]?.slotId ?? ""]} act={act} onClose={() => setSheet(null)} />
      )}
      {sheet?.kind === "route-picker" && (
        <RoutePickerSheet
          planner={planner}
          on={selected}
          onPick={(routeId) => {
            const name = planner.routes.find((r) => r.id === routeId)?.name;
            act(name ? `${name} on ${weekdayName(selected)}` : `No route on ${weekdayName(selected)}`, (s) => placeRoute(s, selected, routeId));
            setSheet(null);
          }}
          onManage={() => setSheet({ kind: "routes" })}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === "routes" && <RoutesSheet
          catalog={catalog}
          planner={planner}
          act={act}
          {...(fixture?.sheet === "route-editor" && planner.routes[0] ? { initialEditing: planner.routes[0] } : {})} onClose={() => setSheet(null)} />}
    </main>
  );
}
